/**
 * Seite „Kosten · offene Zahlungen" - 1:1-Portierung von `renderCostPage` (`index.html:13828-13900`).
 * Klassennamen, Reihenfolge, Texte und Rechenwege sind woertlich uebernommen; ergaenzt wurden
 * ausschliesslich Typen und der Umbau der DB-Bindung auf Props.
 *
 * ================= Der Umbau: globales DB -> Props =================
 *
 * Das Original liest `DB.orders` (ueber `activeReportingOrders()`), `DB.fxDeals` (ueber `effRate`)
 * und `DB.suppliers` (ueber `orderCurrency`) aus dem globalen Zustand und schreibt sein Ergebnis in
 * `#page-costs`. Hier kommen alle drei als Props (`bestellungen`, `deals`, `lieferanten`), die
 * Statusdefinitionen als `definitionen` (Rueckgabe von `setStatusDefinitions(boardSpalten)`), und
 * `openDrawer(id)` wird zu `onOeffnen(id)`.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `overdue` ist nicht durchgehend ein Wahrheitswert (siehe `lib/fx.ts`, Eigenheit 3). Die
 *    KPI-Kachel „Überfällig" faerbt sich ueber `overdue.length ? ... : ...` - eine LEERE Liste ist
 *    hier der Ausschlag, nicht der Summenwert. Eine Liste aus Zeilen mit €-Betrag 0 faerbt die
 *    Kachel also rot, obwohl „0 €" dasteht. Woertlich uebernommen.
 * 2. Die vier KPI-Summen entstehen aus VIER getrennten `reduce`-Laeufen ueber verschiedene
 *    Teilmengen, nicht aus einer gemeinsamen Zwischensumme. Die Gleitkommareihenfolge bleibt damit
 *    dieselbe wie im Original.
 * 3. `byOrder` gruppiert ueber `r.order.id`. Bestellungen OHNE `id` landen alle im selben Eintrag
 *    (Schluessel `undefined`) und erscheinen als EIN Balken. Woertlich uebernommen.
 * 4. Der Naeherungshinweis `.cost-note` erscheint, sobald IRGENDEINE Zeile `approx` ist - nicht je
 *    Zeile. Das `≈` an der einzelnen Zeile steht davon unabhaengig (siehe `PhasenTabelle.tsx`).
 * 5. `activeReportingOrders` filtert archivierte und Split-Status-Bestellungen heraus, BEVOR die
 *    offenen Posten gebildet werden - die Kostenseite zeigt also nicht alle Bestellungen.
 *
 * ================= Was NICHT hierher gehoert =================
 *
 * Die Seite ist rein lesend: kein `useState`, kein Nachladen, kein Zugriff auf `Speicher`. Wer sie
 * einhaengt, reicht die Listen aus dem Speicher herein (siehe Task-Bericht, Abschnitt
 * „Einhaengepunkte") - so bleibt sie ohne Attrappe testbar.
 */
import { EUR0 } from "../../lib/format.ts";
import type { FxDeal } from "../../lib/fx.ts";
import { kostenAuswertung } from "../../lib/kostenAuswertung.ts";
import { LAENDERFILTER_VORGABE } from "../../lib/laender.ts";
import { TRANSPORTARTFILTER_VORGABE } from "../../lib/transportart.ts";
import type { StatusDefinitionen } from "../../lib/status.ts";
import type { Order, Supplier } from "../../lib/types.ts";
import type { LaenderFilter, TransportartFilter } from "../../state/arbeitsplatz.ts";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import { KostenDiagramm } from "./KostenDiagramm.tsx";
import { PhasenTabelle } from "./PhasenTabelle.tsx";

export interface KostenSeiteProps {
  bestellungen: readonly Order[];
  deals: readonly FxDeal[] | null | undefined;
  lieferanten: readonly Supplier[] | null | undefined;
  definitionen: StatusDefinitionen;
  onOeffnen: (bestellungId: string) => void;
  /**
   * Der EU/China-Laenderfilter (Auftrag „EU/China-Laenderfilter in die App-Kopfzeile verschieben",
   * app-weiter Zustand aus `App.tsx`) - wendet `lib/laender.ts#istBestellungNachLaenderFilterSichtbar`
   * auf `active` an, GENAU an der Stelle, an der diese Seite ihre anzuzeigende Bestellliste bildet
   * (index.html:13831-13838).
   *
   * PFLICHT seit der Pruefrunde 2 (Befund W1) - siehe `Board/BoardSeite.tsx#BoardSeiteProps`,
   * Kopfkommentar am selben Feld, fuer die ausfuehrliche Begruendung.
   */
  laenderFilter: LaenderFilter;
  /**
   * Der Luftfracht/Seefracht-Filter (Auftrag „Luftfracht/Seefracht-Filter im Bestellhub",
   * 2026-08-27, app-weiter Zustand aus `App.tsx`) - wendet
   * `lib/transportart.ts#istBestellungNachTransportartFilterSichtbar` an derselben Stelle wie
   * `laenderFilter` an.
   *
   * PFLICHT seit der Nachbesserung zum Transportart-Filter (nach Commit c9da74e) - siehe `Board/BoardSeite.tsx#BoardSeiteProps`,
   * Kopfkommentar am selben Feld, fuer die ausfuehrliche Begruendung (derselbe Befund wie W1 bei
   * `laenderFilter`, hier auf den Transportart-Filter uebertragen).
   */
  transportartFilter: TransportartFilter;
}

export function KostenSeite({
  bestellungen,
  deals,
  lieferanten,
  definitionen,
  onOeffnen,
  laenderFilter = LAENDERFILTER_VORGABE,
  transportartFilter = TRANSPORTARTFILTER_VORGABE,
}: KostenSeiteProps) {
  // Die Rechnung (index.html:13831-13846: aktive Bestellungen, Laender- und Transportartfilter, offene
  // Posten, die vier Summen, die Balken) steht seit dem KI-Zugang (Stufe 1) in
  // `lib/kostenAuswertung.ts` - der Server faehrt dieselbe. Auch die Aufteilung auf die beiden
  // Tabellen (`vorZeilen`, `nachZeilen`) und die Farbregel der Kachel „Ueberfaellig"
  // (`hatUeberfaellige`) kommen fertig von dort; hier bleibt nur die Anzeige.
  const {
    vorZeilen,
    nachZeilen,
    hatUeberfaellige,
    offenGesamt,
    vorEtd,
    nachEtd,
    ueberfaellig,
    naeherung,
    balken,
  } = kostenAuswertung({
    bestellungen,
    deals,
    lieferanten,
    definitionen,
    laenderFilter,
    transportartFilter,
  });

  const eur0 = (v: number): string => EUR0.format(v);

  return (
    <div className="ov-wrap">
      <Seitenkopf titel="Kosten" unterzeile="offene Zahlungen je Bestellung" />
      <div className="kpis">
        <div className="kpi dark">
          <div className="k-label">Offen gesamt (≈ €)</div>
          <div className="k-value">{eur0(offenGesamt)}</div>
        </div>
        <div className="kpi">
          <div className="k-label">Vor ETD fällig</div>
          <div className="k-value">{eur0(vorEtd)}</div>
        </div>
        <div className="kpi">
          <div className="k-label">Nach ETD fällig</div>
          <div className="k-value">{eur0(nachEtd)}</div>
        </div>
        <div className="kpi">
          <div className="k-label">Überfällig</div>
          {/* Eigenheit 1 (Kopfkommentar): die Farbe haengt an der LISTE der ueberfaelligen Zeilen, nicht an der Summe. */}
          <div className="k-value" style={{ color: hatUeberfaellige ? "var(--red)" : "var(--accent-dark)" }}>
            {eur0(ueberfaellig)}
          </div>
        </div>
      </div>
      {naeherung ? (
        <div className="cost-note">
          ≈ = Bestellung ohne gesicherten Kurs; Betrag als $-Wert in die €-Summe übernommen
          (Näherung).
        </div>
      ) : (
        ""
      )}
      <KostenDiagramm balken={balken} onOeffnen={onOeffnen} />
      <PhasenTabelle
        titel="Vor ETD fällig"
        zeilen={vorZeilen}
        klasse="vor"
        lieferanten={lieferanten}
        onOeffnen={onOeffnen}
      />
      <PhasenTabelle
        titel="Nach ETD fällig"
        zeilen={nachZeilen}
        klasse="nach"
        lieferanten={lieferanten}
        onOeffnen={onOeffnen}
      />
    </div>
  );
}
